import { useToast } from '@chakra-ui/react';
import {
  type UseMutationOptions,
  type UseQueryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { type AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';

import { adminInterviewKeys } from '@/features/admin-interview/useAdminInterviewQueries';
import {
  type AccessRequest,
  type ExcelImportRequest,
  type ExcelImportResponse,
  FileService,
  InterviewService,
  type InvitationSettingResponse,
  type InviteCandidateResponse,
  type InviteCandidateViaMailRequest,
  MediaService,
  type ShareCandidatesRequest,
  type UploadFileRequest,
  type UploadFileResponse,
  type UploadUrlPostResponse,
} from '@/services/api';
import {
  type CandidateAttendRequest,
  type DeleteCandidatesFromMultipleInterviewsRequest,
  type InvitationSettingRequest,
  type InviteAllCandidatesViaMailRequest,
  type ReInterviewRequest,
} from '@/services/api/codegen/api';
import {
  OdiBotService,
  type OdiStartRequest,
} from '@/services/api/codegen/api';

const adminCandidatesKeys = {
  getAllCandidatesBase: () => ['getCandidates'] as const,
  getAllInterviewsCandidatesBase: () => ['getAllInterviewsCandidates'] as const,
  getInterviewInvitationSettings: (interviewsId: string) =>
    ['getInterviewInvitationSettings', interviewsId] as const,
};

type CandidatesBulkInviteVariables = {
  interviewId: string;
} & InviteCandidateViaMailRequest;

export const useCandidatesBulkInviteMutation = (
  options?: UseMutationOptions<
    InviteCandidateResponse,
    AxiosError,
    CandidatesBulkInviteVariables
  >
) => {
  const queryClient = useQueryClient();

  return useMutation<
    InviteCandidateResponse,
    AxiosError,
    CandidatesBulkInviteVariables
  >({
    mutationFn: async ({ interviewId, ...body }) => {
      return await InterviewService.putAllInterviewsCandidatesInvite({
        interviewsId: interviewId,
        body: body,
      });
    },
    ...options,
    onSuccess(data, variables, context) {
      queryClient.invalidateQueries({
        queryKey: adminCandidatesKeys.getAllInterviewsCandidatesBase(),
      });
      queryClient.invalidateQueries({
        queryKey: adminCandidatesKeys.getAllCandidatesBase(),
      });
      queryClient.invalidateQueries({
        queryKey: adminInterviewKeys.getInterviewReminders({
          interviewId: variables.interviewId,
          isManual: true,
          page: 0,
          size: 100,
        }),
      });

      options?.onSuccess?.(data, variables, context);
    },
  });
};

export const useRemoveCandidateMutation = (
  options?: Partial<
    UseMutationOptions<
      void,
      AxiosError,
      { interviewsId: string; candidateIds: Array<string> }
    >
  >
) => {
  const queryClient = useQueryClient();

  return useMutation<
    void,
    AxiosError,
    { interviewsId: string; candidateIds: Array<string> }
  >({
    mutationFn: ({ interviewsId, candidateIds }) =>
      InterviewService.deleteInterviewsCandidatesById({
        interviewsId,
        candidateIds,
      }),
    ...options,
    async onSuccess(data, variables, context) {
      await queryClient.invalidateQueries({
        queryKey: adminCandidatesKeys.getAllInterviewsCandidatesBase(),
      });
      await queryClient.invalidateQueries({
        queryKey: adminCandidatesKeys.getAllCandidatesBase(),
      });
      await queryClient.invalidateQueries({
        queryKey: adminInterviewKeys.getInterview({
          interviewsId: variables.interviewsId,
        }),
      });
      options?.onSuccess?.(data, variables, context);
    },
  });
};

export const useFileAccessMutationLegacy = (
  options?: Partial<
    UseMutationOptions<UploadUrlPostResponse, AxiosError, AccessRequest>
  >
) => {
  return useMutation<UploadUrlPostResponse, AxiosError, AccessRequest>({
    mutationFn: (body) =>
      MediaService.postMediaAccess({
        body,
      }),
    ...options,
  });
};

export const useFileAccessMutation = (
  options?: Partial<
    UseMutationOptions<UploadFileResponse, AxiosError, UploadFileRequest>
  >
) => {
  return useMutation<UploadFileResponse, AxiosError, UploadFileRequest>({
    mutationFn: (body) =>
      FileService.postFilesAccess({
        body,
      }),
    ...options,
  });
};

type ExcelImportVariables = {
  interviewId: string;
  body: ExcelImportRequest;
};

type ExcelImportMutationOptions = Partial<
  UseMutationOptions<ExcelImportResponse, AxiosError, ExcelImportVariables>
> & {
  onDuplicatedEmails?: (emails: Array<string>) => void;
};

export const useAnswerExcelImportMutation = (
  options?: ExcelImportMutationOptions
) => {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { t } = useTranslation(['candidate']);

  return useMutation<ExcelImportResponse, AxiosError, ExcelImportVariables>({
    mutationFn: async ({ interviewId, body }) => {
      return await InterviewService.postAllInterviewCandidateExcel({
        interviewId,
        body,
      });
    },
    ...options,
    async onSuccess(data, error, variables) {
      if ((data.invalidItems ?? 0) > 0)
        toast({
          title: t('candidate:importByExcel.invalidCandidateInformation'),
          status: 'error',
        });
      else if ((data.duplicatedItems ?? 0) > 0) {
        if (data.duplicatedEmails && data.duplicatedEmails.length > 0) {
          options?.onDuplicatedEmails?.(data.duplicatedEmails);
        } else {
          toast({
            title: t('candidate:importByExcel.duplicateCandidate'),
            status: 'warning',
          });
        }
      } else {
        toast({
          title: t('candidate:importByExcel.SuccessfullyImported'),
          status: 'success',
        });
      }

      await queryClient.invalidateQueries({
        queryKey: adminCandidatesKeys.getAllInterviewsCandidatesBase(),
      });
      await queryClient.invalidateQueries({
        queryKey: adminCandidatesKeys.getAllCandidatesBase(),
      });

      options?.onSuccess?.(data, error, variables);
    },
  });
};

type ShareCandidateMutationVariables = {
  interviewId: string;
  /** requestBody */
  body?: ShareCandidatesRequest;
};

export const useShareCandidateMutation = (
  options?: Partial<
    UseMutationOptions<void, AxiosError, ShareCandidateMutationVariables>
  >
) => {
  return useMutation<void, AxiosError, ShareCandidateMutationVariables>({
    mutationFn: ({ interviewId, body }) =>
      InterviewService.postAllInterviewsCandidatesShare({ interviewId, body }),
    ...options,
  });
};

type PutInterviewInvitationSettingsMutation = {
  interviewsId: string;
  body?: InvitationSettingRequest;
};

export const usePutInterviewInvitationSettingsMutation = (
  options?: Partial<
    UseMutationOptions<void, AxiosError, PutInterviewInvitationSettingsMutation>
  >
) => {
  return useMutation<void, AxiosError, PutInterviewInvitationSettingsMutation>({
    mutationFn: (body) =>
      InterviewService.putAllInterviewsInvitationSetting(body),
    ...options,
    async onSuccess(data, error, variables) {
      options?.onSuccess?.(data, error, variables);
    },
  });
};

type InterviewAttendRequest = {
  interviewId: string;
  companyId: string;
  body?: CandidateAttendRequest;
};

export const useInterviewAttendRequest = (
  options?: Partial<
    UseMutationOptions<string, AxiosError, InterviewAttendRequest>
  >
) => {
  return useMutation<string, AxiosError, InterviewAttendRequest>({
    mutationFn: (body) => InterviewService.putInterviewsCompanyAttendById(body),
    ...options,
    async onSuccess(data, error, variables) {
      options?.onSuccess?.(data, error, variables);
    },
  });
};

type GetInterviewInvitationSettingsParams = {
  interviewsId: string;
};

export const useGetInterviewInvitationSettingsQuery = (
  params: GetInterviewInvitationSettingsParams,
  options?: Partial<UseQueryOptions<InvitationSettingResponse, AxiosError>>
) => {
  return useQuery<InvitationSettingResponse, AxiosError>({
    queryKey: adminCandidatesKeys.getInterviewInvitationSettings(
      params.interviewsId
    ),
    queryFn: () =>
      InterviewService.getAllInterviewsInvitationSetting(
        params,
        options?.axiosRequestConfig
      ),
    ...options,
  });
};

type InviteAllCandidatesRequest = {
  body?: InviteAllCandidatesViaMailRequest;
};

export const useInviteAllCandidatesRequest = (
  options?: Partial<
    UseMutationOptions<
      InviteCandidateResponse,
      AxiosError,
      InviteAllCandidatesRequest
    >
  >
) => {
  return useMutation<
    InviteCandidateResponse,
    AxiosError,
    InviteAllCandidatesRequest
  >({
    mutationFn: (body) => InterviewService.putInterviewsAll(body),
    ...options,
    async onSuccess(data, error, variables) {
      options?.onSuccess?.(data, error, variables);
    },
  });
};

type DeleteCandidatesRequest = {
  body: DeleteCandidatesFromMultipleInterviewsRequest;
};

export const useBatchDeleteCandidatesRequest = (
  options?: Partial<
    UseMutationOptions<void, AxiosError, DeleteCandidatesRequest>
  >
) => {
  return useMutation<void, AxiosError, DeleteCandidatesRequest>({
    mutationFn: (body) =>
      InterviewService.deleteInterviewsCandidatesBatchDelete(body),
    ...options,
    async onSuccess(data, error, variables) {
      options?.onSuccess?.(data, error, variables);
    },
  });
};

export interface ReInterviewParams {
  interviewsId: string;
  candidateId: string;
  body: ReInterviewRequest;
}

export const useReInterviewMutation = (
  options?: UseMutationOptions<void, AxiosError, ReInterviewParams>
) => {
  const toast = useToast();
  const { t } = useTranslation(['interview']);

  return useMutation<void, AxiosError, ReInterviewParams>({
    mutationFn: ({ interviewsId, candidateId, body }) => {
      return InterviewService.postInterviewsCandidatesReInterviewById({
        interviewsId,
        candidateId,
        body,
      });
    },
    ...options,
    onSuccess(data, variables, context) {
      toast({
        title: t('interview:reInterview.sentSuccess'),
        status: 'success',
      });

      options?.onSuccess?.(data, variables, context);
    },
    onError(err, variables, context) {
      toast({
        title: t('interview:reInterview.sentError'),
        status: 'error',
      });

      options?.onError?.(err, variables, context);
    },
  });
};

export const useOdiBotStartMutation = (
  options?: UseMutationOptions<void, AxiosError, OdiStartRequest>
) => {
  const toast = useToast();
  const { t } = useTranslation(['interview']);
  const queryClient = useQueryClient();
  return useMutation<void, AxiosError, OdiStartRequest>({
    mutationFn: (body) => {
      return OdiBotService.postOdiBotStart({ body });
    },
    ...options,
    onSuccess(data, variables, context) {
      toast({
        title: t('interview:odiBot.startSuccess'),
        status: 'success',
      });

      // invalidate candidate list
      queryClient.invalidateQueries({
        queryKey: adminCandidatesKeys.getAllCandidatesBase(),
      });

      options?.onSuccess?.(data, variables, context);
    },
    onError(err, variables, context) {
      toast({
        title: t('interview:odiBot.startError'),
        status: 'error',
      });

      options?.onError?.(err, variables, context);
    },
  });
};

export const useOdiBotStopMutation = (
  options?: UseMutationOptions<void, AxiosError, OdiStartRequest>
) => {
  const toast = useToast();
  const { t } = useTranslation(['interview']);

  const queryClient = useQueryClient();
  return useMutation<void, AxiosError, OdiStartRequest>({
    mutationFn: (body) => {
      return OdiBotService.postOdiBotStop({ body });
    },
    ...options,
    onSuccess(data, variables, context) {
      toast({
        title: t('interview:odiBot.stopSuccess'),
        status: 'success',
      });

      // invalidate candidate list
      queryClient.invalidateQueries({
        queryKey: adminCandidatesKeys.getAllCandidatesBase(),
      });

      options?.onSuccess?.(data, variables, context);
    },
    onError(err, variables, context) {
      toast({
        title: t('interview:odiBot.stopError'),
        status: 'error',
      });

      options?.onError?.(err, variables, context);
    },
  });
};
